import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class FileCompressionService {
  
  private readonly MAX_SIZE_BYTES = 1 * 1024 * 1024; // 1 MB Threshold

  /**
   * Main entry point to process an uploaded file.
   * If it's an image, it compresses it dynamically.
   * If it's a PDF, it checks if it's already under 1MB.
   */
  public async processFile(file: File): Promise<File> {
    // If the file is already under 1MB, bypass processing completely
    if (file.size <= this.MAX_SIZE_BYTES) {
      return file;
    }

    const fileType = file.type.toLowerCase();

    if (fileType.startsWith('image/')) {
      return this.compressImage(file);
    } 
    
    if (fileType === 'application/pdf') {
      throw new Error(`PDF "${file.name}" exceeds the 1 MB limit (${(file.size / 1024 / 1024).toFixed(2)} MB). Please optimize it before uploading.`);
    }

    throw new Error('Unsupported file format. Only images and PDFs under 1 MB are allowed.');
  }

  /**
   * Compresses an image utilizing an HTML5 Canvas downscale matrix
   */
  private compressImage(file: File): Promise<File> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      
      reader.onload = (event: any) => {
        const img = new Image();
        img.src = event.target.result;

        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // 1. If the image is massive, downscale dimensions proportionally to save memory
          const MAX_PIXEL_DIMENSION = 1920;
          if (width > MAX_PIXEL_DIMENSION || height > MAX_PIXEL_DIMENSION) {
            if (width > height) {
              height = Math.round((height * MAX_PIXEL_DIMENSION) / width);
              width = MAX_PIXEL_DIMENSION;
            } else {
              width = Math.round((width * MAX_PIXEL_DIMENSION) / height);
              height = MAX_PIXEL_DIMENSION;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            return reject(new Error('Failed to get 2D canvas context'));
          }

          // Draw image onto canvas
          ctx.drawImage(img, 0, 0, width, height);

          // 2. Iteratively adjust quality parameter until file is under 1MB
          let quality = 0.9; 
          let base64Data = '';
          
          do {
            // Force export to JPEG or WebP since PNG doesn't support lossy compression quality tags
            const targetType = file.type === 'image/png' ? 'image/jpeg' : file.type;
            base64Data = canvas.toDataURL(targetType, quality);
            quality -= 0.1;
          } while (this.calculateBase64Size(base64Data) > this.MAX_SIZE_BYTES && quality > 0.1);

          // 3. Convert back into a standard binary File blob
          const compressedBlob = this.dataURItoBlob(base64Data);
          const compressedFile = new File([compressedBlob], file.name, {
            type: file.type,
            lastModified: Date.now()
          });

          resolve(compressedFile);
        };

        img.onerror = (err) => reject(err);
      };

      reader.onerror = (err) => reject(err);
    });
  }

  /**
   * Fast math approximation to check base64 stream sizing byte allocations
   */
  private calculateBase64Size(base64String: string): number {
    const padding = (base64String.endsWith('==') ? 2 : (base64String.endsWith('=') ? 1 : 0));
    return (base64String.length * (3 / 4)) - padding;
  }

  /**
   * Helper to convert a Data URI back to a binary Blob
   */
  private dataURItoBlob(dataURI: string): Blob {
    const byteString = atob(dataURI.split(',')[1]!);
    const mimeString = dataURI.split(',')[0]!.split(':')[1]!.split(';')[0]!;
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    
    return new Blob([ab], { type: mimeString });
  }
}